import { environment } from '@/environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, map } from 'rxjs';
import { handleError } from '../tools/handle-error.tools';
import { EChartsOption } from 'echarts';
import { CierreOssa } from '@/app/models/interface/cierre-ossa.model';
import { DescargoOssa } from '@/app/models/interface/descargo-ossa.model';
import { InitOssaForm } from '@/app/models/interface/init-ossa.model';
import { NewOssa } from '@/app/models/interface/new-ossa.model';
import {
  PaginatorModel,
  HasPagination,
} from '@/app/models/interface/paginator.model';
import { CustomResponse } from '@/app/models/interface/response.model';

export interface OssaReport {
  totalGeneral: number;
  total: number;
  abiertas: number;
  cerradas: number;
  groupByTipoHallazgo: AgrupadoOssa[];
  groupByClasificacionHallazgo: AgrupadoOssa[];
  groupByAreaResponsable: AgrupadoOssa[];
  registros: NewOssa[];
  estadoDeCierre: EstadoCierre;
}

export interface EstadoCierre {
  resumen: {
    cerradasEnPlazo: number;
    cerradasFueraDePlazo: number;
    pendienteEnPlazo: number;
    pendienteFueraDePlazo: number;
  };
  porMeses: EstadoCierrePorMes[];
}

export interface EstadoCierrePorMes {
  mes: string;
  index: number;
  cerradasEnPlazo: number;
  cerradasFueraDePlazo: number;
  pendienteEnPlazo: number;
  pendienteFueraDePlazo: number;
  porSemana: EstadoCierrePorSemanaOssa[];
  items: NewOssa[];
  chart: EChartsOption | undefined;
}

export interface EstadoCierrePorSemanaOssa {
  semanaIndex: number;
  cerradasEnPlazo: { value: number; items: NewOssa[] };
  cerradasFueraDePlazo: { value: number; items: NewOssa[] };
  pendienteEnPlazo: { value: number; items: NewOssa[] };
  pendienteFueraDePlazo: { value: number; items: NewOssa[] };
  items: NewOssa[];
}

export interface AgrupadoOssa {
  id: number;
  nombre: string;
  total: number;
  registros: NewOssa[];
}

@Injectable({
  providedIn: 'root',
})
export class OssaService {
  private readonly http = inject(HttpClient);
  private readonly url = environment.api + 'ossa/';

  initOssa(params?: HttpParams) {
    const dir = this.url + 'initOssa/';
    return this.http.get<CustomResponse<InitOssaForm>>(dir, { params }).pipe(
      catchError((err) => handleError(err)),
      map((data) => {
        return data.result;
      }),
    );
  }

  generateReport(params?: HttpParams) {
    const dir = this.url + 'report/';
    return this.http.get<CustomResponse<OssaReport>>(dir, { params }).pipe(
      catchError((err) => handleError(err)),
      map((data) => {
        return data.result;
      }),
    );
  }

  create(formData: FormData) {
    const dir = this.url;
    return this.http.post<CustomResponse<NewOssa>>(dir, formData).pipe(
      catchError((err) => handleError(err)),
      map((data) => {
        return data.result;
      }),
    );
  }

  findAll(params?: HttpParams) {
    return this.http
      .get<CustomResponse<PaginatorModel<NewOssa[], HasPagination>>>(this.url, {
        params,
      })
      .pipe(
        catchError((err) => handleError(err)),
        map((data) => {
          return data.result;
        }),
      );
  }

  findOne(id: number, params?: HttpParams) {
    params = params || new HttpParams();
    params.set('withRelations', 'true');
    return this.http
      .get<CustomResponse<NewOssa>>(this.url + id, { params })
      .pipe(
        catchError((err) => handleError(err)),
        map((data) => {
          return data.result;
        }),
      );
  }

  remove(id: number) {
    return this.http.delete<CustomResponse<NewOssa>>(this.url + id).pipe(
      catchError((err) => handleError(err)),
      map((data) => {
        return data.result;
      }),
    );
  }

  update(id: number, ossa: Partial<NewOssa>) {
    const dir = this.url + id;
    return this.http.patch<CustomResponse<NewOssa>>(dir, ossa).pipe(
      catchError((err) => handleError(err)),
      map((data) => {
        return data.result;
      }),
    );
  }

  updatePut(id: number, ossa: FormData) {
    const dir = this.url + id;
    return this.http.put<CustomResponse<NewOssa>>(dir, ossa).pipe(
      catchError((err) => handleError(err)),
      map((data) => {
        return data.result;
      }),
    );
  }

  /**
   * @description método para descargo ossa
   */

  private readonly urlDescargo = environment.api;

  createDescargo(formData: FormData) {
    const dir = this.urlDescargo + 'ossa-descargo';
    return this.http.post<CustomResponse<DescargoOssa>>(dir, formData).pipe(
      catchError((err) => handleError(err)),
      map((data) => {
        return data.result;
      }),
    );
  }

  updateDescargo(data: Partial<DescargoOssa>) {
    const dir = this.urlDescargo + 'ossa-descargo/' + data.id;
    const { id: _, ...rest } = data;
    return this.http.put<CustomResponse<DescargoOssa>>(dir, rest).pipe(
      catchError((err) => handleError(err)),
      map((data) => {
        return data.result;
      }),
    );
  }

  createCierre(formData: FormData) {
    const dir = this.urlDescargo + 'ossa-cierre';
    return this.http.post<CustomResponse<CierreOssa>>(dir, formData).pipe(
      catchError((err) => handleError(err)),
      map((data) => {
        return data.result;
      }),
    );
  }
}
